import { Box, Button, PlusCircleIcon, Stack } from '@customink/pigment-react';
import type { ProjectItem as ProjectItemType } from '@customink/project-service-react-components';
import {
  useComplementsAllowed,
  useDesigns,
  useMobileBreakpoint,
  useProducts,
  useProject,
  useWCIBMO,
} from '@customink/project-service-react-components';
import { SHIPPING_TYPES } from '@customink/project-service-types';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { EditGroupSizesModal } from './EditGroupSizesModal';
import GroupErrors from './GroupErrors';
import DeliveryOptions from '../DeliveryOptions/DeliveryOptions';
import TurnTimeSelect from '../DeliveryOptions/TurnTimeSelect';
import ShippingSpeed from '../ShippingSpeed';

import type { ModalState } from '@/atoms/editSizesModalAtoms';
import { CartSubtotals } from '@/components/CartSubtotals/CartSubtotals';
import { useOpenCatalogIframe } from '@/components/CatalogIframe';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ProjectItem } from '@/components/ProjectItem/ProjectItem';
import GroupComplements from '@/components/ProjectItemGroup/GroupComplements';
import { ProjectItemListHeader } from '@/components/ProjectItemList';
import { useHasMultipleDesigns } from '@/hooks/Design';
import { useFeatureFlagEnabled } from '@/hooks/FeatureFlags';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { hasValidZipCodeAtom } from '@/hooks/useValidateZipCode';
import { colorSmoke, colorWhite, spacing16 } from '@/utils/designTokens';
import { pageViewTag } from '@/utils/metrics';

type ProjectItemGroupProps = {
  designCid: string;
  projectItems: ProjectItemType[];
  isModalOpen: boolean;
  onOpenModal: (modalState: ModalState) => void;
  onCloseModal: () => void;
};

export const ProjectItemGroup = ({
  designCid,
  projectItems,
  isModalOpen,
  onOpenModal,
  onCloseModal,
}: ProjectItemGroupProps) => {
  const isMobile = useMobileBreakpoint();
  const { data: project } = useProject();
  const colorIds = projectItems
    .map((item) => item.product?.colorId)
    .filter((colorId): colorId is number => colorId !== undefined);

  const { data: productData } = useProducts({
    colorIds,
  });

  const designsById = useDesigns();
  const hasMultipleDesigns = useHasMultipleDesigns();
  const design = designsById?.[designCid];

  const { wcibmo } = useWCIBMO(designCid);
  const quantityType = useOrderQuantityType();
  const hasValidZipCode = useAtomValue(hasValidZipCodeAtom);

  useEffect(() => {
    if (!!wcibmo && hasValidZipCode) {
      pageViewTag(`checkout/summary/${quantityType}/wcibmo/${wcibmo}`);
    }
  }, [hasValidZipCode, quantityType, wcibmo]);

  const onOpenCatalogIframe = useOpenCatalogIframe();

  const handleAddProduct = () => {
    onOpenCatalogIframe({ designCid });
  };

  const showComplements: boolean = useComplementsAllowed(projectItems[0]);

  const shippingSpeedLabel = useMemo(
    () => (hasMultipleDesigns ? 'Shipping Speed' : 'Delivery Options'),
    [hasMultipleDesigns],
  );

  const selectedShippingType = project?.configuration?.shippingType;
  const hideShippingSpeed = useMemo(
    () =>
      selectedShippingType === SHIPPING_TYPES.INDIVIDUAL && hasMultipleDesigns,
    [selectedShippingType, hasMultipleDesigns],
  );

  const addDesignEnabled = useFeatureFlagEnabled('add_design_to_more');
  const handleEditSizes = (modalState: ModalState) => {
    if (!designCid) {
      return;
    }
    onOpenModal(modalState);
  };

  const handleEditAlertGroupSizes = () => {
    handleEditSizes({
      designCid,
      source: 'alert_link',
    });
  };

  return (
    <ErrorBoundary>
      <Box
        data-testid="project-item-group"
        sx={{
          border: `1px solid ${colorSmoke}`,
          backgroundColor: `${colorWhite}`,
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            padding: isMobile
              ? `14px ${spacing16} ${spacing16}`
              : '21px 24px 24px',
            gap: '16px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ProjectItemListHeader
            designCid={designCid}
            projectItems={projectItems}
          />
          <Stack
            sx={{
              gap: '16px',
            }}
          >
            <GroupErrors
              design={design}
              handleEditGroupSizes={handleEditAlertGroupSizes}
              projectItems={projectItems}
            />
            {projectItems.map((item) => (
              <ProjectItem
                item={item}
                key={item.id}
                onEditSizes={(modalState?: ModalState) =>
                  handleEditSizes({
                    ...modalState,
                    designCid,
                    source: 'edit_sizes_link',
                  })
                }
              />
            ))}
            {addDesignEnabled && (
              <Button
                data-testid="add-more-products"
                onClick={handleAddProduct}
                startIcon={<PlusCircleIcon />}
                sx={{ maxWidth: '365px' }}
                variant="secondary"
              >
                Add Design to More Products
              </Button>
            )}
          </Stack>

          {!hideShippingSpeed && (
            <ShippingSpeed title={shippingSpeedLabel}>
              {hasMultipleDesigns ? (
                <>
                  <TurnTimeSelect designCid={designCid} />
                  <CartSubtotals
                    designCid={designCid}
                    projectItems={projectItems}
                  />
                </>
              ) : (
                <DeliveryOptions showTitle={false} />
              )}
            </ShippingSpeed>
          )}
        </Box>
        {!!showComplements && <GroupComplements projectItems={projectItems} />}
        <EditGroupSizesModal
          designCid={designCid}
          isOpen={isModalOpen}
          onClose={onCloseModal}
          onUpdate={onCloseModal}
          products={productData || []}
          projectItems={projectItems}
        />
      </Box>
    </ErrorBoundary>
  );
};
